import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { careersAPI } from "../../services/api";
import Seo from "../../components/Seo";

const STAGE_COLORS = {
  new: "#5e6ad2", screening: "#d97706", interview: "#7c3aed",
  offer: "#0284c7", hired: "#16a34a", rejected: "#dc2626",
};
const STAGE_STEP = { new: 1, screening: 2, interview: 3, offer: 4, hired: 5, rejected: 0 };
const STEP_STAGES = ["new", "screening", "interview", "offer", "hired"];

export default function CareersTracking() {
  const { t, i18n } = useTranslation();
  const { token: urlToken } = useParams();
  const navigate = useNavigate();
  const [inputToken, setInputToken] = useState(urlToken || "");
  const [searchToken, setSearchToken] = useState(urlToken || "");

  const { data: application, isLoading, isError } = useQuery({
    queryKey: ["careers-tracking", searchToken],
    queryFn: () => careersAPI.trackApplication(searchToken).then((r) => r.data),
    enabled: !!searchToken,
    retry: false,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputToken.trim()) {
      setSearchToken(inputToken.trim());
      navigate(`/careers/track/${inputToken.trim()}`, { replace: true });
    }
  };

  const stageColor = application ? (STAGE_COLORS[application.stage] || STAGE_COLORS.new) : null;
  const stageLabel = application ? t(`careers.tracking.stages.${application.stage}`, { defaultValue: application.stage }) : null;
  const currentStep = application ? (STAGE_STEP[application.stage] ?? 0) : 0;
  const isRejected = application?.stage === "rejected";

  return (
    <>
      <Seo
        title="Suivre ma candidature — Mawered SIRH"
        description="Suivez l'état de votre candidature chez Mawered en temps réel."
        path="/careers/track"
        noindex
      />
    <div className="max-w-xl mx-auto space-y-10">
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "#5e6ad2", letterSpacing: "0.12em" }}>
          {t("careers.tracking.eyebrow")}
        </p>
        <h1 className="text-3xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.7px" }}>
          {t("careers.tracking.title")}
        </h1>
      </div>

      {/* Token search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-lg"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
        >
          <Search className="h-4 w-4 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
          <input
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder={t("careers.tracking.searchPlaceholder")}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: "var(--lm-ink)" }}
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2.5 rounded-lg text-sm font-medium"
          style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}
        >
          {t("careers.tracking.search")}
        </button>
      </form>

      {/* Result */}
      {isLoading && searchToken && (
        <div className="py-8 text-center text-sm" style={{ color: "var(--lm-ink-3)" }}>{t("careers.tracking.loading")}</div>
      )}

      {isError && searchToken && (
        <div
          className="px-4 py-3 rounded-lg text-sm"
          style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.20)", color: "#dc2626" }}
        >
          {t("careers.tracking.notFound")}
        </div>
      )}

      {application && (
        <div className="space-y-6">
          {/* Job + stage */}
          <div
            className="rounded-xl p-6 space-y-4"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("careers.tracking.targetPosition")}</p>
                <p className="font-semibold text-sm mt-0.5" style={{ color: "var(--lm-ink)" }}>
                  {application.job_title || t("careers.tracking.positionFallback")}
                </p>
              </div>
              <span
                className="text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0"
                style={{ color: stageColor, backgroundColor: `${stageColor}18`, border: `1px solid ${stageColor}30` }}
              >
                {stageLabel}
              </span>
            </div>

            <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
              {t("careers.tracking.appliedOn", { date: new Date(application.applied_at).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" }) })}
            </p>
          </div>

          {/* Progress steps */}
          {!isRejected ? (
            <div className="space-y-2">
              {STEP_STAGES.map((stage, idx) => {
                const stepNum = idx + 1;
                const isDone = stepNum < currentStep;
                const isActive = stepNum === currentStep;
                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                      style={{
                        backgroundColor: isDone || isActive ? "#5e6ad2" : "var(--lm-s2)",
                        color: isDone || isActive ? "#fff" : "var(--lm-ink-3)",
                        border: `1px solid ${isDone || isActive ? "#5e6ad2" : "var(--lm-border)"}`,
                      }}
                    >
                      {isDone ? "✓" : stepNum}
                    </div>
                    <span
                      className="text-sm"
                      style={{ color: isActive ? "var(--lm-ink)" : isDone ? "var(--lm-ink-2)" : "var(--lm-ink-3)", fontWeight: isActive ? 500 : 400 }}
                    >
                      {t(`careers.tracking.steps.${stage}`)}
                    </span>
                    {isActive && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.12)" }}
                      >
                        {t("careers.tracking.currentStep")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className="rounded-xl px-5 py-4 text-sm"
              style={{ backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", color: "var(--lm-ink-2)" }}
            >
              {t("careers.tracking.rejectedMsg")}
            </div>
          )}
        </div>
      )}

      {!searchToken && (
        <p className="text-sm text-center" style={{ color: "var(--lm-ink-3)" }}>
          {t("careers.tracking.hint")}
        </p>
      )}
    </div>
    </>
  );
}
